import type { PrivacyLevel } from '@seek/ca-myprofile-utils';
import { PrivacyDropdown as ProfileVisibilityDropdown } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Stack, Box } from 'braid-design-system';
import React from 'react';
import { useDispatch } from 'react-redux';

import { CardError } from 'src/components/Card/CardError/CardError';
import { CardTitle } from 'src/components/Card/CardHeading/CardTitle/CardTitle';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { PROFILE_VISIBILITY } from 'src/components/Router';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import { useRetryAction } from 'src/modules/retry-actions/hooks';
import type { Maybe } from 'src/types';
import type { VisibilityLevel } from 'src/types/graphql';

import translations from './.vocab';
import { profileVisibilityDropdownOpened } from './actionCreators';
import {
  useGetProfileVisibilityLevel,
  useUpdateProfileVisibility,
} from './data';

const focusAction = (ref: HTMLButtonElement) => ref.click();

export const ProfileVisibilityContainer = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { profileVisibilityLevel, loading, error } =
    useGetProfileVisibilityLevel();
  // This type cast is safe because the values of the PrivacyLevel and VisibilityLevel enums overlap
  const level = profileVisibilityLevel as unknown as Maybe<PrivacyLevel>;
  const { zone, isUnifiedSite } = useAppConfig();

  const handleProfileVisibilityDropdownToggle = (isOpen: boolean) => {
    if (isOpen) {
      dispatch(profileVisibilityDropdownOpened());
    }
  };

  const levelRef = useFocus<HTMLButtonElement>({
    card: PROFILE_VISIBILITY,
    fieldName: 'level',
    scrollOptions: { desktopOffset: 0, mobileOffset: 0 },
    action: focusAction,
    domReady: !loading && !error,
    forceDesktopScrolling: true,
  });

  const [updateProfileVisibility] = useUpdateProfileVisibility();

  const retryAction = useRetryAction(PROFILE_VISIBILITY);
  const showErrorAlert = Boolean(retryAction?.canRetry);
  const isLoading = Boolean(loading || error);

  return (
    <Box component="section" position="relative" id={PROFILE_VISIBILITY}>
      <Stack space="medium">
        <Stack space="small">
          <CardTitle
            name={PROFILE_VISIBILITY}
            title={t('Profile visibility')}
            loading={isLoading}
          />
          {showErrorAlert ? <CardError card={PROFILE_VISIBILITY} /> : null}
        </Stack>
        {isLoading ? (
          <Stack space="small">
            <LoadingItem type="text" size="standard" lines={3} />
            <Box display="flex" alignItems="center" height="touchable">
              <Box width="full">
                <LoadingItem type="text" size="standard" width="medium" />
              </Box>
            </Box>
            <LoadingItem type="text" size="small" lines={3} />
          </Stack>
        ) : (
          <ProfileVisibilityDropdown
            id="privacyLevel"
            dropdownButtonRef={levelRef}
            {...(level ? { level } : {})}
            onChange={(value: PrivacyLevel) =>
              // This type cast is safe because the values of the PrivacyLevel and VisibilityLevel enums overlap
              updateProfileVisibility({
                profileVisibility: {
                  level: value as unknown as VisibilityLevel,
                },
              })
            }
            onToggle={(isOpen) => handleProfileVisibilityDropdownToggle(isOpen)}
            dropdownToggleComponent="button"
            label={t('Select')}
            zone={zone}
            isUnifiedSite={isUnifiedSite}
          />
        )}
      </Stack>
    </Box>
  );
};
